import { useContext, useState } from 'react';

import { PartnerContact } from '@/components/common/contact_components/PartnerContact';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { UsersContext } from '@/Contexts/UsersContext';
import useContact from '@/hooks/useContact';
import { ContactProps, ContactType, localizedTextEquals } from '@/models/Contact';
import { validateContact } from '@/utils/contactUtils';
import { ProgramNames } from '@/models/Program';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { TabSubPanelContent } from '@/components/myaccount/Applicant_MyAccount/TabSubPanelContent';

import LanguageSelect from './LanguageSelect';
import Grid from '@mui/material/Grid';
import useUser from '@/hooks/useUser';
import { logError } from '@/utils/errorHandlerUtil';

interface AccountSettingsProps {
  onUnsavedChanges?: () => void;
  onSave?: () => void;
}

export default function AccountSettings({ onUnsavedChanges, onSave }: AccountSettingsProps) {
  const { saveContact } = useContact();
  const { updateUserContactId } = useUser();
  const { setAlert } = useContext(SnackbarContext);
  const { currentContact, updateCurrentContact } = useContext(UsersContext);
  const { contactTypes } = useContext(ConfigContext);
  const { translatedText } = useContext(LocaleContext);
  const { selectedProgramId } = useContext(SelectedProgramContext);
  const [savingContact, setSavingContact] = useState<boolean>(false);

  const userContactType = contactTypes.find((c: ContactType) =>
    localizedTextEquals(c.contactTypeName, 'UserContact')
  );

  const handleContactChange = (contact: ContactProps) => {
    updateCurrentContact(contact);
    onUnsavedChanges?.();
  };

  const handleContactSave = async () => {
    if (currentContact == null) return;

    const invalidFields = validateContact(currentContact, userContactType);

    if (invalidFields != null && invalidFields.length > 0) {
      setAlert({
        message: translatedText.myaccount.errorHelperText,
        severity: 'error',
      });
      return;
    }

    setSavingContact(true);
    try {
      const contact = await saveContact(currentContact);
      if (contact != null && contact.id != null) {
        await updateUserContactId(contact.id);

        setAlert({
          message: translatedText.myaccount.updatePrimaryContactSuccess,
          severity: 'success',
        });
        onSave?.();
      } else {
        setAlert({
          message: translatedText.myaccount.updatePrimaryContactError,
          severity: 'error',
        });
      }
    } catch (error) {
      logError(`Error saving contact for user.`, error);
      setAlert({
        message: translatedText.myaccount.updatePrimaryContactError,
        severity: 'error',
      });
    } finally {
      setSavingContact(false);
    }
  };

  return (
    <TabSubPanelContent
      headerText={translatedText.header.mycontact}
      onSave={handleContactSave}
      saveDisabled={savingContact || currentContact == null}
    >
      <Grid container rowSpacing={2} columnSpacing={4} mb={2}>
        {selectedProgramId === ProgramNames.TreeCitiesWorld && (
          <Grid size={{ xs: 12 }}>
            <LanguageSelect />
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <PartnerContact
            id={'user-contact'}
            contact={currentContact}
            emailDisabled={true}
            fieldHelperText={{
              email: translatedText.myaccount.emailNewAccountHelperText,
            }}
            contactType={userContactType}
            onChange={handleContactChange}
          />
        </Grid>
      </Grid>
    </TabSubPanelContent>
  );
}
